import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, MapPin, Phone, Clock } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_LOGO = "https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png";

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 4.8, total_reviews: 279 });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/reviews`);
        setReviews(response.data.reviews);
        setReviewStats({
          average_rating: response.data.average_rating,
          total_reviews: response.data.total_reviews
        });
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div data-testid="reviews-page">
      {/* Hero Section */}
      <section className="py-16 bg-[#14366C]" data-testid="reviews-hero">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-[#00AEEF] mb-4 font-medium">Ratings & Reviews</p>
            <h1 className="font-oswald uppercase text-4xl md:text-5xl font-bold text-white mb-6">
              See What Our Happy Customers Are Saying
            </h1>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Discover why homeowners and businesses trust us as their go-to choice for reliable, high-quality plumbing and heating services in the Twin Cities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Google Reviews Widget - Same as Homepage */}
      <section className="py-16 bg-white" data-testid="google-reviews-section">
        <div className="max-w-6xl mx-auto px-4">
          {/* Google Rating */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src={GOOGLE_LOGO} alt="Google" className="h-7" />
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-[#F5A623] fill-[#F5A623]" />
              ))}
            </div>
            <span className="font-semibold text-[#14366C]">{reviewStats.average_rating}</span>
            <span className="text-gray-500">Rating</span>
          </div>

          {/* Map */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="aspect-[16/9] md:aspect-[21/9] w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2822.7!2d-92.963!3d44.963!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87f7d6a2678eea6b%3A0x53893f88d32197f2!2sBruce%20Nelson%20Plumbing%20%26%20Heating%20Services%2C%20LLC.!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BNPH Location Map"
              />
            </div>
          </div>

          {/* Read Reviews Button */}
          <div className="text-center mb-12">
            <a
              href="https://www.google.com/search?q=Bruce+Nelson+Plumbing+%26+Heating+Services+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#137333] hover:bg-[#0f5a28] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              <span className="text-white font-bold">G</span>
              Read Our Google Reviews
            </a>
          </div>

          {/* Review Cards - Show all reviews */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={review.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-5 h-5 ${i < review.rating ? 'text-[#F5A623] fill-[#F5A623]' : 'text-gray-300'}`} />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-[#14366C]">{review.author}</p>
                  <p className="text-sm text-gray-500">{review.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Info Section */}
      <section className="py-16 bg-[#F8F8F8]" data-testid="business-info-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="font-oswald uppercase text-2xl font-bold text-[#14366C] mb-6 text-center">
              Bruce Nelson Plumbing & Heating Services
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <MapPin className="w-8 h-8 text-[#F58220] mb-3" />
                <p className="text-gray-700">7012 6th St. N.</p>
                <p className="text-gray-700">Oakdale, MN 55128</p>
              </div>

              <div className="flex flex-col items-center">
                <Phone className="w-8 h-8 text-[#F58220] mb-3" />
                <a 
                  href="tel:651-738-9354" 
                  className="text-gray-700 hover:text-[#F58220] transition-colors font-semibold text-lg"
                >
                  (651) 738-9354
                </a>
              </div>

              <div className="flex flex-col items-center">
                <Clock className="w-8 h-8 text-[#F58220] mb-3" />
                <p className="text-gray-700">Mon - Fri: 7AM - 4PM</p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://maps.google.com/maps?q=Bruce+Nelson+Plumbing+%26+Heating+Services+7012+6th+St+N+Oakdale+MN"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#14366C] hover:bg-[#0d2a52] text-white font-oswald font-bold uppercase tracking-wider px-6 py-3 transition-colors"
              >
                Get Directions
              </a>
              <Link 
                to="/contact"
                className="bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-6 py-3 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Leave Review CTA */}
      <section className="py-12 bg-[#14366C]" data-testid="leave-review-cta">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-white mb-4">
              Had a Great Experience?
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              We'd love to hear from you! Leave us a review and let others know about your experience with BNPH.
            </p>
            <a
              href="https://g.page/r/CfKXMdOIPJNTEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-8 py-3 transition-colors"
            >
              Leave a Review <Star className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
